import { Component, useLayoutEffect, useRef } from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { usePrefersCreateRoot } from 'react-create-root-preference';

// See https://github.com/reactwg/react-18/discussions/5#discussioncomment-2276079
import { jsx as _jsx } from "react/jsx-runtime";
function RenderCallback({
  children,
  callback
}) {
  const once = useRef(false);
  useLayoutEffect(() => {
    if (once.current) return;
    once.current = true;
    callback();
  }, [callback]);
  return children;
}
class PortalLayerComponent extends Component {
  componentWillUnmount() {
    const {
      element,
      renderTree,
      prefersCreateRoot,
      setCloseTransitionRootCleanup
    } = this.props;
    const closeTransitionTree = renderTree(null);
    if (!closeTransitionTree) return;
    const firstChild = element.firstChild;
    if (!firstChild || !firstChild.children) return;
    const originalTransitionContents = Array.from(firstChild.children).map(childElement => childElement.cloneNode(true));
    const closeTransitionContainer = document.createElement('div');
    element.appendChild(closeTransitionContainer);
    const appendTransitionContents = () => {
      originalTransitionContents.forEach(el => {
        if (closeTransitionContainer.firstChild !== null) {
          closeTransitionContainer.firstChild.appendChild(el);
        }
      });
    };
    if (prefersCreateRoot) {
      const closeTransitionRoot = createRoot(closeTransitionContainer);
      // The root outlives this component; the parent Layer unmounts it once the
      // close transition completes (or on its own unmount).
      setCloseTransitionRootCleanup(() => {
        closeTransitionRoot.unmount();
        setCloseTransitionRootCleanup(null);
      });
      closeTransitionRoot.render( /*#__PURE__*/_jsx(RenderCallback, {
        callback: appendTransitionContents,
        children: closeTransitionTree
      }));
    } else {
      // Default import, not `import { render }`, so this builds on both React 18
      // and 19 (React 19 drops the named export; a named import would be a hard
      // bundler error). Only reached on React 18, where prefersCreateRoot=false.
      // eslint-disable-next-line react/no-deprecated
      ReactDOM.render(closeTransitionTree, closeTransitionContainer, appendTransitionContents);
    }
  }
  render() {
    const {
      children,
      element,
      renderTree
    } = this.props;
    const tree = renderTree(children);
    return /*#__PURE__*/createPortal(tree, element);
  }
}
PortalLayerComponent.displayName = 'PortalLayer';
function PortalLayer(props) {
  const prefersCreateRoot = usePrefersCreateRoot();
  return /*#__PURE__*/_jsx(PortalLayerComponent, Object.assign({}, props, {
    prefersCreateRoot: prefersCreateRoot
  }));
}
export default PortalLayer;